/** The one image source a page holds: a StarScribe image path on this site (gyro-core's allowedImageSrc). */
const IMAGE_SRC = /^\/v1\/starscribe\/pages\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\/images\/[A-Za-z0-9_-]{1,64}$/;
export function isPageImageSrc(src) {
    return typeof src === 'string' && IMAGE_SRC.test(src);
}
// =====================================================================================================================
// Bytes and errors
// =====================================================================================================================
export function toBase64(bytes) {
    let text = '';
    for (let i = 0; i < bytes.length; i += 0x8000)
        text += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
    return btoa(text);
}
export function fromBase64(text) {
    const raw = atob(text);
    const out = new Uint8Array(raw.length);
    for (let i = 0; i < raw.length; i++)
        out[i] = raw.charCodeAt(i);
    return out;
}
export function errorInfo(error, fallback = 'That did not work. Try again in a moment.') {
    if (!error || typeof error !== 'object')
        return { message: fallback };
    const value = error;
    const status = typeof value.status === 'number' ? value.status : undefined;
    const code = typeof value.code === 'string' ? value.code : undefined;
    const message = typeof value.message === 'string' && value.message.trim() && status !== undefined ? value.message.trim() : fallback;
    return { ...(status !== undefined ? { status } : {}), ...(code ? { code } : {}), message };
}
// =====================================================================================================================
// The API
// =====================================================================================================================
const SEGMENT = /^[A-Za-z0-9_-]{1,64}$/;
function segment(value, what) {
    if (!SEGMENT.test(value))
        throw new Error(`Invalid StarScribe ${what}.`);
    return value;
}
function versionNumber(n) {
    if (!Number.isInteger(n) || n < 1)
        throw new Error('Invalid StarScribe version.');
    return String(n);
}
function readGrant(raw) {
    if (!raw || typeof raw !== 'object')
        return null;
    const value = raw;
    const email = typeof value.email === 'string' ? value.email : '';
    if (!email)
        return null;
    return {
        email,
        name: typeof value.name === 'string' && value.name ? value.name : email,
        role: value.role === 'edit' ? 'edit' : 'view',
        ...(typeof value.grantId === 'string' ? { grantId: value.grantId } : {}),
        ...(typeof value.grantedAtMs === 'number' ? { grantedAtMs: value.grantedAtMs } : {}),
        ...(typeof value.expiresAtMs === 'number' ? { expiresAtMs: value.expiresAtMs } : {}),
    };
}
/** GET .../shares (and every share change's answer): `{ owner: { email, name }, shares: [...] }`, read field by field. */
function readShares(raw) {
    const value = raw && typeof raw === 'object' ? raw : {};
    const owner = value.owner && typeof value.owner === 'object' ? value.owner : undefined;
    return {
        ...(owner && typeof owner.email === 'string' && owner.email
            ? { owner: { email: owner.email, name: typeof owner.name === 'string' && owner.name ? owner.name : owner.email } }
            : {}),
        shares: Array.isArray(value.shares) ? value.shares.map(readGrant).filter((grant) => grant !== null) : [],
    };
}
function readThreads(raw) {
    const threads = raw && typeof raw === 'object' ? raw.threads : undefined;
    return Array.isArray(threads) ? threads : undefined;
}
function readPeople(raw) {
    const value = raw && typeof raw === 'object' ? raw : {};
    const people = [];
    for (const item of Array.isArray(value.people) ? value.people : []) {
        if (!item || typeof item !== 'object')
            continue;
        const person = item;
        if (typeof person.email !== 'string' || !person.email)
            continue;
        people.push({ email: person.email, ...(typeof person.name === 'string' && person.name ? { name: person.name } : {}) });
    }
    return { people, complete: value.complete !== false };
}
/** The StarScribe API over a host's transport. */
export function starscribeApi(transport) {
    const page = (pageId) => `/v1/starscribe/pages/${segment(pageId, 'page')}`;
    const request = (path, options) => transport.request(path, options);
    const post = (path, body = {}) => request(path, { method: 'POST', body });
    let mePromise;
    return {
        transport,
        me() {
            if (!mePromise) {
                mePromise = request('/v1/starscribe/me');
                mePromise.catch(() => { mePromise = undefined; });
            }
            return mePromise;
        },
        listPages: (which, q, signal) => request('/v1/starscribe/pages', { query: { which, ...(q ? { q } : {}) }, ...(signal ? { signal } : {}) }),
        createPage: (body) => post('/v1/starscribe/pages', body),
        getPage: (pageId, signal) => request(page(pageId), signal ? { signal } : {}),
        renamePage: (pageId, title) => request(page(pageId), { method: 'PATCH', body: { title } }),
        deletePage: (pageId) => request(page(pageId), { method: 'DELETE' }),
        recoverPage: (pageId) => post(`${page(pageId)}/recover`),
        purgePage: (pageId) => request(`${page(pageId)}/purge`, { method: 'DELETE' }),
        stream: (pageId, query, signal) => transport.events(`${page(pageId)}/stream`, { query: { sv: query.sv, clientId: query.clientId }, signal }),
        postUpdates: (pageId, body) => post(`${page(pageId)}/updates`, body),
        postAwareness: async (pageId, body) => { await post(`${page(pageId)}/awareness`, body); },
        append: (pageId, body) => post(`${page(pageId)}/append`, body),
        versions: (pageId, signal) => request(`${page(pageId)}/versions`, signal ? { signal } : {}),
        version: (pageId, n, signal) => request(`${page(pageId)}/versions/${versionNumber(n)}`, signal ? { signal } : {}),
        restoreVersion: (pageId, n) => post(`${page(pageId)}/versions/${versionNumber(n)}/restore`),
        undo: (pageId, version) => post(`${page(pageId)}/undo`, { version }),
        applyCard: (pageId, cardId) => post(`${page(pageId)}/cards/${segment(cardId, 'card')}/apply`),
        dismissCard: (pageId, cardId) => post(`${page(pageId)}/cards/${segment(cardId, 'card')}/dismiss`),
        applyAllCards: (pageId) => post(`${page(pageId)}/cards/apply-all`),
        keepPreview: (pageId, previewId) => post(`${page(pageId)}/previews/${segment(previewId, 'preview')}/keep`),
        revertPreview: (pageId, previewId) => post(`${page(pageId)}/previews/${segment(previewId, 'preview')}/revert`),
        shares: async (pageId, signal) => readShares(await request(`${page(pageId)}/shares`, signal ? { signal } : {})),
        share: async (pageId, body) => readShares(await post(`${page(pageId)}/shares`, body)),
        setShareRole: async (pageId, grantId, role) => {
            const answer = await request(`${page(pageId)}/shares/${segment(grantId, 'grant')}`, { method: 'PATCH', body: { role } });
            return readGrant(answer?.share);
        },
        unshare: async (pageId, grantId) => {
            const answer = await request(`${page(pageId)}/shares/${segment(grantId, 'grant')}`, { method: 'DELETE' });
            return { shared: answer?.shared === true };
        },
        comments: async (pageId, signal) => ({ threads: readThreads(await request(`${page(pageId)}/comments`, signal ? { signal } : {})) ?? [] }),
        addComment: async (pageId, body) => readThreads(await post(`${page(pageId)}/comments`, body)),
        reply: async (pageId, threadId, body) => readThreads(await post(`${page(pageId)}/comments/${segment(threadId, 'thread')}/replies`, body)),
        resolveThread: async (pageId, threadId) => readThreads(await post(`${page(pageId)}/comments/${segment(threadId, 'thread')}/resolve`)),
        reopenThread: async (pageId, threadId) => readThreads(await post(`${page(pageId)}/comments/${segment(threadId, 'thread')}/reopen`)),
        deleteMessage: async (pageId, threadId, messageId) => readThreads(await request(`${page(pageId)}/comments/${segment(threadId, 'thread')}/messages/${segment(messageId, 'message')}`, { method: 'DELETE' })),
        exportPage: (pageId, format) => post(`${page(pageId)}/export`, { format }),
        people: async (query, signal) => readPeople(await request('/v1/starscribe/people', { query: { q: query }, ...(signal ? { signal } : {}) })),
        canSaveFiles: typeof transport.saveArtifact === 'function',
        async saveArtifact(artifact) {
            if (!transport.saveArtifact)
                throw new Error('This window cannot save files.');
            await transport.saveArtifact(artifact);
        },
        canLoadImages: typeof transport.blob === 'function',
        canUploadImages: typeof transport.upload === 'function',
        async image(src, signal) {
            if (!isPageImageSrc(src))
                throw new Error('Not a page image.');
            if (!transport.blob)
                throw new Error('This window cannot show page images.');
            return transport.blob(src, signal ? { signal } : {});
        },
        async uploadImage(pageId, file, contentType) {
            if (!transport.upload)
                throw new Error('Images cannot be added to a page here.');
            const answer = await transport.upload(`${page(pageId)}/images`, await file.arrayBuffer(), contentType);
            if (!answer || typeof answer.src !== 'string' || !isPageImageSrc(answer.src))
                throw new Error('The image could not be added. Try again in a moment.');
            return { src: answer.src };
        },
    };
}
/** A readable name from an address when no name is known: "dana.ortiz@…" reads "Dana Ortiz"; anything else stays as is. */
export function displayName(nameOrEmail) {
    const value = nameOrEmail.trim();
    const at = value.indexOf('@');
    if (at <= 0)
        return value;
    const words = value.slice(0, at).split(/[._-]+/).filter(Boolean);
    if (words.length < 2)
        return value;
    return words.map((word) => word[0].toUpperCase() + word.slice(1)).join(' ');
}
//# sourceMappingURL=api.js.map